import { z } from "zod";
import {
  DomainModelMetaExtendedSchema,
  DomainModelMetaSchema,
  DomainModelReferenceSchema,
  DomainModelSchema,
} from "../util/index.ts";

// Legacy user types and schemas:

export const LegacyUserClaimSchema = z
  .object({
    name: z.string(),
    email: z.string().email(),
    site_ids: z.array(z.number()),
  })
  .merge(DomainModelSchema);

export type LegacyUserClaimType = z.infer<typeof LegacyUserClaimSchema>;

// User v2 (Node API auth) status types and schemas:

export const UserStatus = {
  active: "active",
  disabled: "disabled",
} as const;
export type UserStatusType = (typeof UserStatus)[keyof typeof UserStatus];

export const UserTypeEnum = {
  human: "human",
  machine: "machine",
} as const;
export type UserTypeEnumType = (typeof UserTypeEnum)[keyof typeof UserTypeEnum];

const BaseUserSchema = z
  .object({
    name: z.string(),
    status: z.enum([UserStatus.active, UserStatus.disabled]),
    type: z.enum([UserTypeEnum.human, UserTypeEnum.machine]).optional(),
    timezone: z.string(),
  })
  .merge(DomainModelSchema)
  .merge(DomainModelMetaExtendedSchema);

export const HumanUserSchema = BaseUserSchema.extend({
  type: z.literal(UserTypeEnum.human).optional(),
  email: z.string().email(),
  /** @deprecated - used to maintain backward compatibility. */
  quicksight: z.boolean().optional(),
  can_access_quicksight_dashboards: z.boolean().optional(),
  can_access_quicksight_console: z.boolean().optional(),
  phone: z.string().nullable(),
});

export const MachineUserSchema = BaseUserSchema.extend({
  type: z.literal(UserTypeEnum.machine).optional(),
});

export const CompanyIdSchema = z.object({
  company_id: z.string().uuid(),
});
export type CompanyIdType = z.infer<typeof CompanyIdSchema>;

export const UserReturnTypeSchema = HumanUserSchema.extend({
  type: z.nativeEnum(UserTypeEnum),
}).merge(CompanyIdSchema);
export type UserReturnType = z.infer<typeof UserReturnTypeSchema>;

const CompaniesListSchema = z.array(
  z.object({ id: z.string(), name: z.string() })
);

export const UserWithCompaniesSchema = UserReturnTypeSchema.merge(
  CompanyIdSchema
).extend({
  companies: CompaniesListSchema,
});
export type UserWithCompaniesType = z.infer<typeof UserWithCompaniesSchema>;

export const ICompanyMembershipSchema = z
  .object({
    company_id: z.string().uuid(),
    user_id: z.string().uuid(),
  })
  .merge(DomainModelSchema)
  .merge(DomainModelMetaExtendedSchema);

export type ICompanyMembership = z.infer<typeof ICompanyMembershipSchema>;

const CompanyMembershipArraySchema = z.array(ICompanyMembershipSchema);

export const UserWithGroupsSchema = UserReturnTypeSchema.merge(
  z.object({
    user_groups: z.array(DomainModelReferenceSchema),
    user_groups_count: z.number(),
    roles: z.array(DomainModelReferenceSchema),
    roles_count: z.number(),
    company_membership: CompanyMembershipArraySchema,
  })
);
export type UserWithGroupsType = z.infer<typeof UserWithGroupsSchema>;

export const MembershipSchema = z
  .object({
    company_id: z.string().uuid(),
    role_id: z.string().uuid(),
    user_id: z.string().uuid(),
  })
  .merge(DomainModelSchema)
  .merge(DomainModelMetaExtendedSchema);
export type Membership = z.infer<typeof MembershipSchema>;

export const MembershipUserSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string().email(),
});

export const MembershipWithUserSchema = MembershipSchema.extend({
  user: MembershipUserSchema,
});
export type MembershipWithUser = z.infer<typeof MembershipWithUserSchema>;

export const CreateHumanUserInputSchema = HumanUserSchema.pick({
  id: true,
  name: true,
  email: true,
  phone: true,
  quicksight: true,
  can_access_quicksight_dashboards: true,
  can_access_quicksight_console: true,
  status: true,
  type: true,
  timezone: true,
})
  .partial({
    id: true,
    phone: true,
  })
  .extend({ type: z.literal(UserTypeEnum.human) });
export type CreateHumanUserInput = z.input<typeof CreateHumanUserInputSchema>;

export const CreateMachineUserInputSchema = MachineUserSchema.pick({
  id: true,
  name: true,
  status: true,
  type: true,
  timezone: true,
})
  .strict()
  .partial({ id: true })
  .extend({ type: z.literal(UserTypeEnum.machine) });
export type CreateMachineUserInput = z.input<
  typeof CreateMachineUserInputSchema
>;

export const CreateUserInputSchema = z.discriminatedUnion("type", [
  CreateHumanUserInputSchema,
  CreateMachineUserInputSchema,
]);
export type CreateUserInput = z.input<typeof CreateUserInputSchema>;

export const UpdateHumanUserInputSchema = CreateHumanUserInputSchema.pick({
  name: true,
  phone: true,
  quicksight: true,
  can_access_quicksight_dashboards: true,
  can_access_quicksight_console: true,
  status: true,
  timezone: true,
}).partial();
export type UpdateHumanUserInput = z.input<typeof UpdateHumanUserInputSchema>;

export const UpdateMachineUserInputSchema = CreateMachineUserInputSchema.pick({
  name: true,
  status: true,
  timezone: true,
}).partial();
export type UpdateMachineUserInput = z.input<
  typeof UpdateMachineUserInputSchema
>;

export const UpdateUserInputSchema = UpdateHumanUserInputSchema.or(
  UpdateMachineUserInputSchema
);
export type UpdateUserInput = z.input<typeof UpdateUserInputSchema>;

// `company_membership` is an object that's only available on /users/search API:

const CompanyMembershipObjectSchema = z.array(
  z
    .object({
      company_id: z.string().uuid(),
    })
    .merge(DomainModelMetaSchema)
    .merge(DomainModelMetaExtendedSchema)
);

export type CompanyMembershipType = z.infer<
  typeof CompanyMembershipObjectSchema
>;

/**
 * @deprecated Use HumanUserSchema / MachineUserSchema and UserReturnType for new code.
 * Represents the legacy single User model in the Node API.
 */
export const UserSchema = z
  .object({
    name: z.string(),
    /** @deprecated - used to maintain backward compatibility.  */
    quicksight: z.boolean().optional(),
    can_access_quicksight_dashboards: z.boolean().optional(),
    can_access_quicksight_console: z.boolean().optional(),
    // IMPROVE: make this an enum
    timezone: z.string(),
    // IMPROVE: add phone number regex (depends on country though...which is a data point we currently don't track)
    phone: z.string().nullable(),
    status: z.enum([UserStatus.active, UserStatus.disabled]),
    email: z.string().email(),
    user_groups: z.array(DomainModelReferenceSchema),
    user_groups_count: z.number(),
    roles: z.array(DomainModelReferenceSchema),
    roles_count: z.number(),
  })
  .merge(DomainModelSchema)
  .merge(DomainModelMetaSchema)
  .merge(DomainModelMetaExtendedSchema)
  .describe("Represents a User model in the Node API");

export const UserCreateSchema = UserSchema.pick({
  name: true,
  email: true,
  phone: true,
  timezone: true,
  quicksight: true,
  can_access_quicksight_dashboards: true,
  can_access_quicksight_console: true,
});

export const UserUpdateSchema = UserSchema.pick({
  name: true,
  phone: true,
  timezone: true,
  quicksight: true,
  can_access_quicksight_dashboards: true,
  can_access_quicksight_console: true,
}).partial();

/** @deprecated Prefer UserReturnType for get/create/update responses. */
export type UserType = z.infer<typeof UserSchema>;

export type UserCreateType = z.infer<typeof UserCreateSchema>;
export type UserUpdateType = z.infer<typeof UserUpdateSchema>;
